import React, { useState } from "react";
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  List,
  ListItem,
  ListItemText,
  Chip,
  Typography,
} from "@mui/material";
import {
  GenerationalChain,
  GenerationalArtOrder,
  checkChainCompleteness,
} from "../../utils/generationalArtUtils";

interface OrderConfigurationActionsProps {
  order: GenerationalArtOrder;
  selectedChain: GenerationalChain | undefined;
  saving: boolean;
  onSave: (orderId: string) => void;
}

const OrderConfigurationActions: React.FC<OrderConfigurationActionsProps> = ({
  order,
  selectedChain,
  saving,
  onSave,
}) => {
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    incompleteMembers: any[];
  }>({
    open: false,
    incompleteMembers: [],
  });

  const handleSaveClick = () => {
    if (!selectedChain) {
      return;
    }

    const completeness = checkChainCompleteness(
      selectedChain,
      order.maxGenerations
    );

    if (!completeness.isComplete) {
      // Show confirmation dialog for incomplete chain
      setConfirmationDialog({
        open: true,
        incompleteMembers: completeness.incompleteMembers,
      });
    } else {
      // Chain is complete, proceed with save
      onSave(order.paypalOrderId);
    }
  };

  const handleConfirmSave = () => {
    onSave(order.paypalOrderId);
    setConfirmationDialog({
      open: false,
      incompleteMembers: [],
    });
  };

  const handleCancelSave = () => {
    setConfirmationDialog({
      open: false,
      incompleteMembers: [],
    });
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gap={2}>
        {/* Help text when no chain is selected */}
        {!selectedChain && (
          <Alert severity="info" sx={{ mb: 1 }}>
            <Box component="span" sx={{ fontSize: "0.875rem" }}>
              Please select a family line above to configure your generational
              art.
            </Box>
          </Alert>
        )}

        <Box display="flex" gap={2} justifyContent="flex-end">
          <Button
            type="button"
            variant="contained"
            onClick={handleSaveClick}
            disabled={!selectedChain || saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
            sx={{
              opacity: !selectedChain || saving ? 0.6 : 1,
              cursor: !selectedChain || saving ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Saving..." : "Complete Order Configuration"}
          </Button>

          <Button type="button" variant="outlined" href="/account?tab=orders">
            View All Orders
          </Button>
        </Box>
      </Box>

      {/* Confirmation Dialog for Incomplete Chains */}
      <Dialog
        open={confirmationDialog.open}
        onClose={handleCancelSave}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          ⚠️ Incomplete Chain Warning
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ mb: 2 }}>
            The selected chain has incomplete information for some family
            members. This may affect the quality of your generational art piece.
          </DialogContentText>

          <Typography variant="h6" gutterBottom>
            Missing Information:
          </Typography>

          <List dense>
            {confirmationDialog.incompleteMembers.map((member, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        component="span"
                        sx={{ fontSize: "1rem", fontWeight: "bold" }}
                      >
                        {member.name || "Unknown Name"}
                      </Box>
                      <Chip
                        label={`Generation ${member.generation || index + 1}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      {(!member.imageMetadata ||
                        member.imageMetadata.includes("Placeholder")) && (
                        <Chip
                          label="Missing Photo"
                          size="small"
                          color="warning"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      )}
                      {(!member.name || member.name.trim() === "") && (
                        <Chip
                          label="Missing Name"
                          size="small"
                          color="error"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      )}
                      {(!member.birthdate ||
                        member.birthdate.trim() === "") && (
                        <Chip
                          label="Missing Birth Date"
                          size="small"
                          color="error"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Box component="span" sx={{ fontSize: "0.875rem" }}>
              <strong>Recommendation:</strong> Consider adding the missing
              information in your Family Tree before proceeding. You can still
              submit this configuration, but the final artwork may include
              placeholder images or incomplete information.
            </Box>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSave} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSave}
            color="warning"
            variant="contained"
            autoFocus
          >
            Submit Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderConfigurationActions;
